import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import isArray from 'lodash/isArray';
import sortBy from 'lodash/sortBy';
import { WWWCompanyDocument } from '../../common/documents/WWWCompanyDocument';
import { WWWCompanyEditDocument } from '../../common/documents/WWWCompanyEditDocument';

/** Convert a company document into the same form as the payload to the edit function */
export const convertCompanyDocumentToEditable = (document: WWWCompanyDocument): WWWCompanyEditDocument => ({
  ...document,
  projectTypes: document.projectTypes.map((projectType) => ({ id: projectType.projectTypeID })),
  contractingForms: document.contractingForms.map((contractingForm) => ({ id: contractingForm.contractingFormID })),
  customerTargetGroups: document.customerTargetGroups.map((customerTargetGroup) => ({ id: customerTargetGroup.targetGroupID })),
  machineryTypes: document.machineryTypes.map((machineryType) => ({
    id: machineryType.machineryTypeID,
    count: machineryType.count,
    range: machineryType.range,
  })),
  taitoCompetences: document.taitoCompetences.map((taitoCompetence) => ({
    id: taitoCompetence.competenceTypeID,
    totalAmountRange: taitoCompetence.totalAmountRange,
  })),
  languages: document.languages.map((language) => ({ id: language.languageID })),
  talo80: document.talo80.map((talo80) => ({ id: talo80.talo80ID })),
  operationalAreas: document.operationalAreas.map((operationalArea) => ({ id: operationalArea.areaID })),
});

/** Compare company document field values */
export const compareCompanyDocumentFields = (a: any, b: any) => {
  /** Sanitize field data before comparison, this is necessary if Hookstate is used */
  const sa = JSON.parse(JSON.stringify(a));
  const sb = JSON.parse(JSON.stringify(b));

  if (isArray(sa) && isArray(sb)) {
    return isEqual(sortBy(sa), sortBy(sb));
  } else {
    return isEqual(sa, sb);
  }
};

/**
 * Compare values of two company edit documents and return all changed fields
 * @param a The company document to be compared
 * @param b The original document to compare against
 * @param section List of keys to be compared
 * @returns A document with all changed fields or false if nothing changed
 */
export const compareCompanyDocuments = <T extends keyof WWWCompanyEditDocument>(
  a: Partial<WWWCompanyEditDocument>,
  b: Partial<WWWCompanyEditDocument>,
  section: T[] | null = null
) => {
  const document = section ? pick(a, section as any) : { ...a };
  const documentKeys = Object.keys(document);
  const changedDocument: Partial<Pick<WWWCompanyEditDocument, T>> = {};
  const originalDocument = b;

  if (documentKeys.length) {
    for (const key of documentKeys) {
      if (document.hasOwnProperty(key) && !compareCompanyDocumentFields(originalDocument[key], document[key])) {
        /** A value has changed, mark it as changed */
        changedDocument[key] = document[key];
      }
    }
  }

  return Object.keys(changedDocument).length ? changedDocument : false;
};
